/**
 * Wellness rollup formatting — shared, and deliberately free of React.
 *
 * These three lived on VitalsCard, which was fine while a component was the
 * only consumer. The Apple Watch bridge needs them too: the phone formats the
 * rollup once and ships display-ready strings, so the watch never reimplements
 * per-key rules and the two surfaces can't drift into describing the same day
 * differently.
 *
 * But `useWatchSync` importing a component module would drag React Navigation,
 * AsyncStorage, the Ably context and the icon set into the bridge hook's module
 * graph — and into every jest suite that touches it. Plain functions belong in
 * a plain module. VitalsCard re-exports them so existing importers are
 * unaffected.
 */

export const WELLNESS_STATUS_STYLE: Record<string, { label: string; color: string }> = {
  active_now: { label: 'Active now', color: '#10B981' },
  active_recently: { label: 'Active recently', color: '#10B981' },
  quiet_expected: { label: 'Quiet', color: '#6B7280' },
  unusually_quiet: { label: 'Quieter than usual', color: '#F59E0B' },
  signals_offline: { label: 'Signals offline', color: '#6B7280' },
  paused: { label: 'Paused', color: '#6B7280' },
};

/**
 * Describe the configured unusual-quiet cooldown in plain language.
 *
 * This is user-facing rather than a settings abbreviation: "6 hours" makes
 * the consequence of the action clearer than "6h", while exact mixed values
 * remain honest if a plan carries a non-preset configuration. Missing settings
 * return null so the UI does not guess at a plan-specific promise.
 */
export function wellnessCooldownDuration(value: unknown): string | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const minutes = Math.round(parsed);
  if (minutes <= 0) return null;

  const unit = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`;
  if (minutes % 10080 === 0) return unit(minutes / 10080, 'week');
  if (minutes % 1440 === 0) return unit(minutes / 1440, 'day');
  if (minutes % 60 === 0) return unit(minutes / 60, 'hour');

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) return `${unit(hours, 'hour')} ${unit(remainingMinutes, 'minute')}`;
  return unit(minutes, 'minute');
}

/** Copy for acknowledging an unusual-quiet episode without claiming it is resolved. */
export function quietCheckInPauseCopy(cooldownMinutes: unknown) {
  const duration = wellnessCooldownDuration(cooldownMinutes);
  return {
    action: duration ? `Pause quiet check-ins for ${duration}` : 'Pause quiet check-ins',
    accessibilityHint: 'Stops any remaining check-in steps and waits before another quiet check-in can start.',
    confirmation: `Quiet check-ins paused${duration ? ` for ${duration}` : ''}. The “Quieter than usual” status may remain while the signals are still quiet.`,
  };
}

export function relTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  // Floor, never round: "1h ago" at 1h59m understates staleness politely,
  // but "2h ago" at 1h31m would overstate recency's inverse — floor is the honest bound.
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * How stale a DAY-CUMULATIVE number has to be before it stops being safe to
 * present bare.
 *
 * Deliberately far tighter than SOURCE_OFFLINE_HOURS.health (18h), and
 * deliberately NOT the same knob: 18h asks "is this device still reporting at
 * all", which drives status and alerting and should not move. This asks a
 * different question — "is this number still a description of today?"
 *
 * A point-in-time reading survives being old. "76 bpm" measured at 07:00 is
 * still a true reading of 76 bpm. A day-cumulative count does not: "1,098 steps
 * today" synced at 07:00 and read at 23:00 is not a stale fact, it is the wrong
 * number for the day being asked about, and it reads as alarmingly low for
 * someone who has in fact been walking around since.
 *
 * 90 minutes is roughly the point where a normally-syncing device has clearly
 * missed a window, without stamping "as of" on every reading during ordinary
 * background-task jitter.
 */
export const DAY_COUNT_STALE_MINUTES = 90;

/**
 * How old the last location point can be before "home so far" stops being
 * stated bare.
 *
 * The server only calls location `source_offline` after 24 HOURS
 * (rollup constants: SOURCE_OFFLINE_HOURS.location), so anything fresher lands
 * in `pending` — and `pending` was rendering as a flat "home so far". A phone
 * last heard from yesterday evening therefore produced a sentence that reads
 * as a positive statement about where someone is right now.
 *
 * Three hours is the point past which "no departure seen" stops being evidence
 * of anything: it is long enough not to nag a phone that reports on movement
 * rather than on a timer, and short enough that a reader is told before they
 * act on it.
 */
export const LOCATION_STALE_MINUTES = 180;

/** Minutes since an ISO timestamp, or null when absent/unparseable. */
export function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

/**
 * Clock time in the viewer's locale — "7:04am". Used to stamp a cumulative
 * count with the moment it stopped being complete, because "16h ago" makes the
 * reader do the subtraction and get it wrong.
 */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function vitalSummary(v: any): string {
  switch (v.key) {
    case 'moved_today': {
      if (v.state === 'source_offline') return `no sync ${relTime(v.last_sync_at) || 'yet'}`;
      if (v.steps_today == null) return 'no steps yet';
      const steps = `${v.steps_today.toLocaleString()} steps`;
      // Stamp the count with when it stopped counting. Without this the number
      // silently claims to cover the whole day up to now.
      const stale = minutesSince(v.last_sync_at);
      if (stale != null && stale >= DAY_COUNT_STALE_MINUTES) {
        const at = clockTime(v.last_sync_at);
        return at ? `${steps} as of ${at}` : `${steps} (${relTime(v.last_sync_at)})`;
      }
      return steps;
    }
    case 'meds_today':
      if (v.expected == null || v.expected === 0) return v.self_marked > 0 ? `${v.self_marked} marked` : 'none scheduled';
      return `${v.self_marked}/${v.expected} marked`;
    case 'app_activity':
      return v.last_seen_at ? relTime(v.last_seen_at) : 'not yet today';
    case 'out_and_about': {
      if (v.last_transition) return `${v.last_transition.type === 'arrived' ? 'arrived' : 'out'} ${relTime(v.last_transition.at)}`;
      if (v.state === 'source_offline') return 'no location data';
      // "home so far" is an assertion about where someone is, and it was being
      // made on location data up to 24 hours old — the server's own offline
      // threshold. When the last point is stale, say when we last heard
      // instead of implying we know now. Same treatment `moved_today` above
      // already gives a cumulative count, for the same reason.
      const stale = minutesSince(v.last_point_at);
      if (stale != null && stale >= LOCATION_STALE_MINUTES) {
        const at = clockTime(v.last_point_at);
        return at ? `home as of ${at}` : `home so far (${relTime(v.last_point_at)})`;
      }
      // No point at all, but not yet old enough to be called offline — this is
      // a plan whose location sharing has never reported. Do not call that
      // "home".
      if (v.last_point_at == null) return 'no location yet';
      return 'home so far';
    }
    default:
      return v.state;
  }
}
